import React from "react";
import { Link, useLocation } from "react-router-dom";
import { getGitHubAuthUrl } from "../api";
import { useAuth } from "../auth/AuthContext";
import GitHubIcon from "./GitHubIcon";

/**
 * The one real sign-in screen (item 20) — reached from three places that
 * used to diverge: the marketing nav's "Sign in" link (was a direct external
 * bypass), ProtectedRoute's bounce (was contextless and silently dropped
 * `from`), and the 401 interceptor's expired-session redirect (used to go to
 * "/" with zero messaging). All three now land here with situational copy.
 *
 * No split panel, unlike /signup — a returning user doesn't need re-selling
 * on the product, just a fast, low-friction way back in (confirmed with the
 * project owner). Reconfirmed 2026-08-25 when /signup's glow/gradient polish
 * made this page look bland by comparison: still single-column (the
 * no-re-selling reasoning holds), but now carries the same gradient wash +
 * card glow treatment as /signup, just without the marketing panel.
 */
function Login() {
  const location = useLocation();
  const { rememberRedirectDestination } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const reason = searchParams.get("reason");
  const from = location.state?.from;

  const handleLogin = () => {
    // Stashed in sessionStorage, not GitHub's `state` param — `state` is
    // already reserved for invite codes (item 15) and reusing it here would
    // collide with that mechanism. AuthContext reads this back once the
    // GitHub round-trip completes.
    if (from) {
      rememberRedirectDestination(from);
    }
    window.location.href = getGitHubAuthUrl();
  };

  // Item 24: `deleted` is a confirmation, not an error — someone who just
  // deleted their account needs to see that it actually happened. The
  // "Start free trial" cross-link below doubles as the path back if they
  // change their mind.
  const subhead =
    reason === "expired"
      ? "Your session expired — sign in again to continue."
      : reason === "deleted"
      ? "Your Anthurium account is scheduled for deletion. Sign in within 30 days to restore it."
      : "Welcome back — let's get back to reviewing.";

  // Issue #73: deleting a sole member's account also deletes the team and
  // uninstalls the GitHub App. The uninstall runs after the commit and is
  // non-fatal, so it can fail while the deletion itself succeeded — and the
  // confirmation dialog promised it would happen. ProfilePage carries the fact
  // here because it logs the user out immediately and has nowhere left to
  // put it.
  //
  // A generic link, not a deep link to the installation: the id would have had
  // to ride in this URL and land in 30-day CloudFront access logs, and someone
  // signed out of Anthurium can find it on this page anyway.
  const appKept = reason === "deleted" && searchParams.get("app") === "kept";

  return (
    <div className="min-h-screen bg-rf-bg-primary relative isolate flex items-center justify-center overflow-hidden">
      {/* Page-wide ambient wash — same two-corner teal/blue recipe as
          /signup's left panel, translated to a single centered layout
          instead of a tall marketing column. `isolate` here from the
          start — /signup's equivalent glow shipped without it and
          rendered fully invisible (a -z-10 layer with no stacking-context
          boundary on its `relative` parent paints behind the page's own
          root background, regardless of opacity; see CLAUDE.md gotcha #32). */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(50% 50% at 20% 15%, rgba(0,212,170,0.10) 0%, transparent 65%), radial-gradient(50% 50% at 80% 85%, rgba(0,153,255,0.08) 0%, transparent 65%)",
        }}
        aria-hidden="true"
      />

      <div className="relative isolate mx-4 w-full max-w-md">
        {/* Glow behind the card — /signup's own card-glow recipe, giving
            this single card the same atmospheric depth /signup's PR card
            has instead of floating flat against the page background. */}
        <div
          className="absolute -inset-8 -z-10 rounded-3xl blur-3xl opacity-40"
          style={{
            background:
              "radial-gradient(60% 60% at 50% 40%, rgba(0,212,170,0.18) 0%, rgba(0,153,255,0.08) 45%, transparent 75%)",
          }}
          aria-hidden="true"
        />

        <div className="bg-rf-bg-secondary rounded-2xl p-10 w-full border border-rf-border text-center shadow-2xl">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-rf-accent to-rf-accent-blue flex items-center justify-center text-white font-bold text-lg">
              A
            </div>
            <span className="text-2xl font-bold text-rf-text-primary tracking-tight">
              Anthurium
            </span>
          </div>

          {/* Tagline */}
          <h1 className="text-xl font-semibold text-rf-text-primary mb-2">
            Sign in to Anthurium
          </h1>
          <p className="text-rf-text-secondary text-sm mb-8 leading-relaxed">
            {subhead}
          </p>

          {appKept && (
            <p
              data-testid="app-still-installed"
              className="text-sm text-rf-accent-warning mb-6 text-center"
            >
              We couldn't remove the Anthurium GitHub App from your organization. You can remove it
              yourself from{" "}
              <a
                href="https://github.com/settings/installations"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-rf-text-primary transition-colors"
              >
                your GitHub installation settings
              </a>
              .
            </p>
          )}

          {/* Login Button */}
          <button
            onClick={handleLogin}
            className="w-full py-3 px-6 rounded-lg bg-white text-gray-900 font-semibold text-sm
                          hover:bg-gray-100 transition-all duration-200 flex items-center justify-center gap-3"
          >
            <GitHubIcon />
            Sign in with GitHub
          </button>

          {/* Cross-link — item 20: catches a wrong guess in one click, before
              any GitHub round-trip. Symmetric with /signup's own "Already have
              an account? Log in" link. */}
          <p className="text-rf-text-secondary text-sm mt-6">
            New to Anthurium?{" "}
            <Link to="/signup" className="text-rf-accent font-semibold hover:underline">
              Start free trial
            </Link>
          </p>

          {/* Footer. Item 24 added the legal line here too, not just on
              /signup: per gotcha #29 a brand-new visitor can create an
              account through this button just as easily, since auth-callback
              upserts a users row either way. */}
          <p className="text-rf-text-muted text-xs mt-6 leading-relaxed">
            By signing in, you authorize Anthurium to access your repositories,
            and you agree to our{" "}
            <Link to="/terms" className="underline hover:text-rf-text-secondary">
              Terms
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="underline hover:text-rf-text-secondary">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
